import React, { useRef, useState } from "react";
import piexif from "piexifjs"; // stripe exif metadaa
import Resizer from "react-image-file-resizer"; // resize image
import { useStateContext } from "../contexts/ContextProvider";
import noImage from "../data/images/no-image4.jpeg";
import { apiUpdateUser } from "../services/apiService";
import { FaCirclePlus, FaSpinner } from "react-icons/fa6";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage } from "../services/firebaseConfig";
import { getRandomString } from "../helpers/randomString";

const ManageAvatar = (props) => {
  const { onSuccess } = props;
  const { user, setUser } = useStateContext();
  const [isAvatarSaved, setIsAvatarSaved] = useState(false);
  const [imageUploadError, setImageUploadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const maxFileSizeMB = 1;

  const fileInputRef = useRef(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    setIsLoading(true);
    const image = event.target.files[0];
    if (
      image.size <= maxFileSizeMB * 1024 * 1024 &&
      image.type.match("image/*")
    ) {
      const resizedFile = await resizeFile(image);
      const strippedAndResizedFile = await removeExifData(resizedFile);

      // define storge location, and new file name
      const imageRef = ref(
        storage,
        `avatars/${user.uuid}/${getRandomString(20)}.${
          image.name.split(".")[1]
        }`
      );

      // upload file to cloud
      await uploadBytes(imageRef, strippedAndResizedFile)
        .then(async (snapshot) => {
          await getDownloadURL(snapshot.ref)
            .then((url) => {
              // collect url, and add to array
              saveAvatar(url);
            })
            .catch((error) => {
              setIsLoading(false);
              console.log("firebase error 2: ", error);
            });
        })
        .catch((error) => {
          setImageUploadError(true);
          setIsLoading(false);
          console.log("firebase error 1: ", error);
        });
    } else {
      setImageUploadError(true);
      resetImageErrors();
      setIsLoading(false);
    }
  };

  const saveAvatar = async (url) => {
    const oldImage = user.avatar;
    user.avatar = url;
    const response = await apiUpdateUser(user.id, user);
    if (response.hasError) {
      console.log(response.error);
      setIsLoading(false);
      return;
    } else {
      setUser(response.data);
      setIsAvatarSaved(true);
      setIsLoading(false);
      resetImageSuccess();
      if (oldImage) removeOldImage(oldImage);
    }
  };

  // remove metadata
  const removeExifData = async (imageFile) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = function (e) {
        const dataUrl = e.target.result;
        try {
          // stipes metadata from base64 image
          const strippedDataUrl = piexif.remove(dataUrl);

          // convert base64 back to blob
          const byteString = atob(strippedDataUrl.split(",")[1]);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          const blob = new Blob([ab], { type: "image/jpeg" });

          // return blob for upload
          resolve(blob);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = function (error) {
        reject(error);
      };
      reader.readAsDataURL(imageFile);
    });
  };

  // resize files
  const resizeFile = (file) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file, // The image file to be resized
        800, // maxWidth of the resized image
        800, // maxHeight of the resized image
        "JPEG", // compressFormat (e.g., "JPEG", "PNG", "WEBP")
        80, // quality (0-100)
        0, // rotation degree
        (uri) => {
          resolve(uri); // Callback function returning the resized image URI (base64)
        },
        "file" // outputType (e.g., "base64", "blob", "file")
      );
    });

  const removeOldImage = (url) => {
    console.log(url);
    // delete image from image server
    const imageRef = ref(storage, url);
    getDownloadURL(imageRef)
      .then(() => {
        // File exists, proceed with deletion
        deleteObject(imageRef)
          .then(() => {
            console.log("File deleted successfully!");
          })
          .catch((error) => {
            console.error("Error deleting file:", error);
          });
      })
      .catch((error) => {
        // File does not exist or other error occurred
        if (error.code === "storage/object-not-found") {
          console.log("File does not exist, no need to delete.");
        } else {
          console.error("Error checking file existence:", error);
        }
      });
  };

  const resetImageSuccess = () => {
    setTimeout(() => {
      setIsAvatarSaved(false);
    }, 2000);
    clearTimeout();
  };

  const resetImageErrors = () => {
    setTimeout(() => {
      setImageUploadError(false);
    }, 1000);
    clearTimeout();
  };

  return (
    <div>
      <div className="flex justify-center mt-8">
        <div className="h-28 w-28 rounded-full bg-gray-300">
          {!isLoading && (
            <img
              src={
                user.avatar instanceof Blob
                  ? URL.createObjectURL(user.avatar)
                  : user.avatar || noImage
                //remove this logic once image handling is setup
              }
              alt={`${user.firstName}`}
              className="h-28 w-28 object-cover rounded-full drop-shadow-xl cursor-pointer"
              onClick={handleClick}
            />
          )}
        </div>
        <form className="relative">
          <label>
            <FaCirclePlus
              className="absolute bottom-0 right-0 text-2xl text-snappily-orange bg-white
                          border-white border-2 rounded-full cursor-pointer"
            />
            {isLoading && (
              <FaSpinner className="absolute bottom-10 right-10 text-3xl text-snappily-green animate-spin" />
            )}

            <input
              id="avatar"
              name="avatar"
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
        </form>
      </div>
      <div className="flex justify-center h-5">
        {isAvatarSaved && (
          <div className="text-center text-blue-500 mt-0 px-20">
            Avatar Saved
          </div>
        )}
        {imageUploadError && (
          <div className="text-center text-red-500 mt-1 px-20">
            Image Upload Error: Please try again.
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageAvatar;
